/**
 * DESTINIA, GRAVÉ.
 *
 * Le mot n'est pas composé dans un `h1` : il est dessiné en SVG, rempli d'un or filé à trois
 * arrêts, et CREUSÉ par le filtre `gravure` — une lumière en haut, une ombre en bas, l'inverse
 * exact du relief des interfaces. Au-dessus, la devise en petites capitales très espacées ; de part
 * et d'autre, un filet et une losange.
 *
 * LES ORNEMENTS NE VIENNENT QU'AVEC LA DEVISE, et ce n'est pas un caprice : au frontispice, ils
 * encadrent ; dans l'enseigne, à trente-huit pixels de haut, ils viennent buter contre les hastes
 * du mot et l'on ne lit plus qu'une bouillie dorée. Un ornement qui gêne la lecture du nom est un
 * ornement de trop.
 *
 * L'éclat traverse le mot UNE fois, 1,2 s après l'ouverture, et plus jamais (design, « La marque »).
 * Sous `prefers-reduced-motion`, il ne passe pas du tout — et rien ne manque, parce qu'il ne
 * raconte rien qu'on ne voie déjà.
 */
import { useId } from "react";
import { t } from "../lexique/langue.js";

interface Props {
  /** La devise ne s'affiche qu'à la porte : ailleurs, le mot suffit et la place est au texte. */
  devise?: string;
  /** L'éclat ne passe qu'à la première apparition de la page — pas à chaque nœud. */
  eclat?: boolean;
  hauteur?: number;
}

const LARGEUR = 640;
const CORPS = 84;

export function Logo({ devise, eclat = false, hauteur = 120 }: Props) {
  // `useId` produit des identifiants à deux-points ; ils sont valides en HTML5 mais fâchent les
  // sélecteurs, et l'on s'en sert dans des `url(#…)`. On les rend sages.
  const clef = useId().replace(/:/g, "-");
  const clip = `coupe-${clef}`;

  // La ligne de pied du mot, et la hauteur totale. Sans devise, le mot occupe tout : c'est la
  // version d'enseigne, réduite à l'essentiel.
  const pied = devise ? 150 : 96;
  const hauteurVue = devise ? 208 : 118;

  return (
    <svg
      className="logo"
      viewBox={`0 0 ${LARGEUR} ${hauteurVue}`}
      style={{ height: `${hauteur}px`, width: "auto" }}
      role="img"
      aria-label={devise ? `Destinia — ${devise}` : "Destinia"}
    >
      <defs>
        <clipPath id={clip}>
          <text className="logo__mot" x={LARGEUR / 2} y={pied} textAnchor="middle" fontSize={CORPS}>
            {t("DESTINIA")}
          </text>
        </clipPath>
      </defs>

      {devise ? (
        <>
          <text className="logo__devise" x={LARGEUR / 2} y="30" textAnchor="middle" fontSize="13">
            {devise}
          </text>

          {/* Le filet du haut, coupé au milieu par une losange : la marque d'un frontispice. */}
          <g className="logo__ornement" transform="translate(0 62)">
            <path d="M132 0h158" />
            <path d="M350 0h158" />
          </g>
          <path className="logo__losange" d="M320 55l11 7-11 7-11-7z" />
        </>
      ) : null}

      <text className="logo__mot" x={LARGEUR / 2} y={pied} textAnchor="middle" fontSize={CORPS}>
        {t("DESTINIA")}
      </text>

      {eclat ? (
        <g clipPath={`url(#${clip})`}>
          <polygon className="logo__eclat" points="150,40 270,40 190,220 70,220" />
        </g>
      ) : null}

      {devise ? (
        <>
          <g className="logo__ornement" transform="translate(0 186)">
            <path d="M206 0h96" />
            <path d="M338 0h96" />
          </g>
          <circle className="logo__losange" cx="320" cy="186" r="3.5" />
        </>
      ) : null}
    </svg>
  );
}
